/**
 * viewport.ts — phone layout helpers.
 *
 * One breakpoint decides the layout: below Tailwind's `md` (768 px) the app is
 * a phone app (one column, bottom navigation, full-screen sheets). Desktop
 * styles use `md:` and up, phone-only styles use `max-md:`, so this query and
 * the CSS always agree.
 */
import { useEffect, useRef, useSyncExternalStore } from 'react';

export const PHONE_QUERY = '(max-width: 767px)';

function phoneQuery(): MediaQueryList | null {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(PHONE_QUERY)
    : null;
}

export function isPhoneViewport(): boolean {
  return phoneQuery()?.matches ?? false;
}

function subscribe(onChange: () => void): () => void {
  const mq = phoneQuery();
  if (!mq) return () => {};
  mq.addEventListener('change', onChange);
  return () => mq.removeEventListener('change', onChange);
}

/** True while the window is phone-sized; re-renders when that changes. */
export function useIsPhone(): boolean {
  return useSyncExternalStore(subscribe, isPhoneViewport, () => false);
}

/** The on-screen keyboard is assumed open when it takes more than this share of the window. */
const KEYBOARD_SHARE = 0.25;

/**
 * Keeps the app exactly as tall as the visible area.
 *
 * iOS Safari doesn't shrink the layout when the keyboard opens; it slides the
 * page up instead, which hides the composer's top or the header. Tracking
 * `visualViewport` and sizing the shell to it (`--app-height`) keeps the
 * composer just above the keyboard on iOS and Android alike. While the
 * keyboard is open, `<html data-keyboard="open">` lets CSS drop the bottom
 * navigation so the conversation keeps the room.
 */
export function watchVisualViewport(win: Window = window): () => void {
  const vv = win.visualViewport;
  const root = win.document.documentElement;
  if (!vv) return () => {};
  const update = () => {
    root.style.setProperty('--app-height', `${Math.round(vv.height)}px`);
    const keyboardOpen = win.innerHeight - vv.height > win.innerHeight * KEYBOARD_SHARE;
    if (keyboardOpen) root.dataset['keyboard'] = 'open';
    else delete root.dataset['keyboard'];
    // iOS scrolls the page to reveal a focused field; the shell already fits, so undo it
    if (win.scrollY !== 0) win.scrollTo(0, 0);
  };
  update();
  vv.addEventListener('resize', update);
  vv.addEventListener('scroll', update);
  return () => {
    vv.removeEventListener('resize', update);
    vv.removeEventListener('scroll', update);
    root.style.removeProperty('--app-height');
    delete root.dataset['keyboard'];
  };
}

let detailScreens = 0;

/**
 * Marks a phone "detail" screen (an open conversation, channel or mail).
 * While one is shown, `<html data-detail="open">` hides the top bar and the
 * tab bar, so the screen's own header with its Back button takes over.
 * No effect from `md` up: the CSS that reads it is phone-only.
 */
export function useMobileDetail(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    const root = document.documentElement;
    detailScreens += 1;
    root.dataset['detail'] = 'open';
    return () => {
      detailScreens -= 1;
      if (detailScreens === 0) delete root.dataset['detail'];
    };
  }, [active]);
}

/**
 * Phones: makes the system Back button (Android, iOS swipe) close a detail
 * screen that has no URL of its own, such as a team channel or a mail.
 * Opening adds a history entry; Back pops it and calls `close`. Closing from
 * the UI removes the entry again, so Back never lands on a dead step.
 */
export function usePhoneBackStep(open: boolean, close: () => void): void {
  const closeRef = useRef(close);
  closeRef.current = close;
  useEffect(() => {
    if (!open || !isPhoneViewport()) return;
    window.history.pushState({ ...(window.history.state as object | null), phoneDetail: true }, '');
    const onPop = () => closeRef.current();
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      if ((window.history.state as { phoneDetail?: boolean } | null)?.phoneDetail) window.history.back();
    };
  }, [open]);
}
